/**
 * Populates article-count superscripts in the blog tag navigation.
 *
 * Each `.blog-tag-nav` declares a `data-counts-url` pointing at the blog's
 * `?view=tag-counts` JSON endpoint. The counts are fetched once per URL,
 * cached in sessionStorage, and injected into the matching `[data-tag-count]`
 * elements. Failures degrade gracefully (counts stay hidden).
 */

const CACHE_PREFIX = 'blog-tag-counts:';

/** @type {Map<string, Promise<Record<string, number>>>} */
const inflight = new Map();

/**
 * @param {string} url
 * @returns {Promise<Record<string, number>>}
 */
async function loadCounts(url) {
  const cacheKey = CACHE_PREFIX + url;

  try {
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) return JSON.parse(cached);
  } catch {
    // sessionStorage unavailable or malformed cache — fall through to fetch.
  }

  let promise = inflight.get(url);
  if (!promise) {
    promise = fetch(url, { headers: { Accept: 'application/json' } })
      .then((response) => {
        if (!response.ok) throw new Error(`Request failed: ${response.status}`);
        return response.text();
      })
      .then((text) => {
        const data = JSON.parse(text);
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify(data));
        } catch {
          // Ignore storage write failures (private mode / quota).
        }
        return data;
      })
      .finally(() => {
        inflight.delete(url);
      });

    inflight.set(url, promise);
  }

  return promise;
}

/**
 * @param {HTMLElement} nav
 */
async function populate(nav) {
  const url = nav.dataset.countsUrl;
  if (!url) return;

  /** @type {NodeListOf<HTMLElement>} */
  const slots = nav.querySelectorAll('[data-tag-count]');
  if (!slots.length) return;

  let counts;
  try {
    counts = await loadCounts(url);
  } catch {
    return;
  }

  for (const slot of slots) {
    const key = slot.dataset.tagCount;
    if (!key) continue;

    const value = counts[key];
    if (typeof value === 'number' && value > 0) {
      slot.textContent = String(value);
    } else {
      slot.textContent = '';
    }
  }
}

function init() {
  /** @type {NodeListOf<HTMLElement>} */
  const navs = document.querySelectorAll('.blog-tag-nav[data-counts-url]');
  navs.forEach((nav) => {
    populate(nav);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
